import { Component, Injector, OnInit } from '@angular/core';
import {
  ActivationMnpInfo,
  AvailableOperator,
  AvailableOperators,
  Cart,
  DynamicEntityKey, EntityParsed, Price,
  StaticEntityKey,
  TotalPriceEnum,
  ValidationError
} from 'models';
import {combineLatest} from 'rxjs';
import { debounceTime, filter, first, pairwise, tap, withLatestFrom } from 'rxjs/operators';
import { ModalType } from 'src/app-shared/models/modal.interface';
import { ProcessStepBaseComponent } from '../../_core/process-step-base.component';

const REQUEST_LIMIT = 4;
const WINDTRE_OPERATOR = 'WIN3';

@Component({
  selector: 'sb-activation-otp',
  templateUrl: './activation-otp.component.html',
  styleUrls: ['./activation-otp.component.scss']
})
export class ActivationOtpComponent extends ProcessStepBaseComponent implements OnInit {
  public MnpOperators: AvailableOperator[] = [];
  private unlistedOperator: AvailableOperator =  {
    id: 'NOTLISTED',
    name: '- Altro operatore',
    donatingCode: '00'
  };
  public disableOtpRequest: boolean;
  public showTutorial: boolean;
  public shouldShowIccidModal: boolean;
  public resOtpMaxSendRetry: number;
  public resOtpValidityMin: number;
  public maxSendFrequency: string;
  private mnpMsisdnInfo: string;
  private mnpOperatorInfo: string;
  private mnpEsimInfo: boolean;

  constructor(injector: Injector) {
    super(injector);
  }

  get shouldShowIccid() {
    return this.formGroup.get('activationMnpInfo.operator').value === WINDTRE_OPERATOR;
  }

  ngOnInit() {
    super.isStickyNavigation();
    super.setFormGroup(this.formsService.generateStepActivationOtpForm());
    super.setFormStoreDataFunction(this.appStoreFacadeService.step.storeStepActivationOtpValue);
    this.getFormDataAndValidation();
    this.manageOtpErrors();
    this.getMnpData();
  }

  public openOtpModal(otpError: any = null, showNotification) {
    const formGroup = this.formGroup;
    let mobileNumber = formGroup.get('activationMnpInfo.msisdn').value;
    if (mobileNumber) {
      mobileNumber = mobileNumber.toString().replace(/(\d{3})/, '$1 ');
    }
    this.analyticsService.sendCustomEvent({
      custom_label: '/popup/inserisci-codice-otp'
    });
    this.appStoreFacadeService.modals.openModal(
      ModalType.OtpModal,
      {
        title: 'ENTITY.ACTIVATION-MNP-INFO.MODAL_OTP_TITLE',
        options: {
          error: otpError,
          number: mobileNumber,
          notification: showNotification,
          otpValidityMin: this.resOtpValidityMin,
          otpMaxSendRetry: this.resOtpMaxSendRetry,
          sendFrequency: this.maxSendFrequency === 'HOUR' ? 'ora' : 'giorno',
          sendOtp: (shouldSubmit) => {
            // Redo the same process
            if (!shouldSubmit) {
              this.requestOtp(true);
            } else {
              this.goToNextStep();
            }
          },
          reset() {
            formGroup.get('activationMnpInfo.msisdn').reset();
            formGroup.get('activationMnpInfo.operator').reset();
          }
        },
        resolve: (confirm) => {
          if (confirm) {
            /* try {
              this.analyticsService.sendGenericAction(AnalyticsEventActions.OTP_SUBMIT, AnalyticsEventCategories.OTP_MESSAGE);
            } catch (ex) {
              console.warn(ex);
            } */
            if (!formGroup.get('activationMnpInfo.esim').value) {
              formGroup.get('activationMnpInfo.esim').setValue(false);
            }
            this.appStoreFacadeService.entity.storeEntityValue(DynamicEntityKey.ActivationMnpInfo,
              formGroup.get('activationMnpInfo').value);
            return super.goToNextStep();
          }
        }
      }
    );
  }

  public goToNextStep() {
    if ( this.isFormDifferent() ) {
      this.requestOtp(false);
    } else {
      window.localStorage.setItem('carrier-origin', this.formGroup.get('activationMnpInfo.operator').value);
      return super.goToNextStep();
    }
  }

  public requestOtp(showNotification) {
    const { msisdn, operator } = this.formGroup.get('activationMnpInfo').value;
    let operatorName = '';
    if (operator) {
      operatorName = (this.MnpOperators.find(r => r.id.toString() === operator) || {} as any).name;
    }

    this.execService.requestOtp(msisdn, operatorName)
      .subscribe(resp => {
        let otpError;
        this.resOtpMaxSendRetry = resp && resp.otpInfo && resp.otpInfo.maxSendRetry || 10;
        this.resOtpValidityMin = resp && resp.otpInfo && resp.otpInfo.validityMin || 10;
        this.maxSendFrequency = resp && resp.otpInfo && resp.otpInfo.maxSendFrequency || 'DAY';
        // NOTE: CHECK IF WE NEED TO ADD THE TIMEOUT FOR DISABLE THE OTP REQUEST AND FOR ERROR MESSAGES
        if (!resp) {
          this.appStoreFacadeService.errors.openErrorModal('MODALS.ERROR.TEMPORARY', 'temporary-error-modal', '');
          return;
        }
        if (resp.error) {
          otpError = resp.error.code.split('|')[0].toUpperCase();
          console.warn('otp modal error', otpError);
          if (otpError === 'TEMPORARY.ERROR') {
            this.appStoreFacadeService.errors.openErrorModal('MODALS.ERROR.TEMPORARY', 'temporary-error-modal', '');
            return;
          }
          if (otpError === 'OTP_MAX_LIMIT_REQUEST_REACHED') {
            this.appStoreFacadeService.navigation.navigateToWarningPage({
              title: 'ENTITY.ACTIVATION-MNP-INFO.OTP_ERROR.OTP_MAX_LIMIT_REQUEST_REACHED.TITLE',
              description: 'ENTITY.ACTIVATION-MNP-INFO.OTP_ERROR.OTP_MAX_LIMIT_REQUEST_REACHED.TEXT',
            });
            return;
          }
        }
        this.openOtpModal(otpError, showNotification);
      });
  }

  private getFormDataAndValidation() {
    window.localStorage.removeItem('carrier-origin');
    this.subscriptions.add(
      this.appStoreFacadeService.entity.availableOperators$
        .pipe(first(Boolean))
        .subscribe((availableOperators: AvailableOperators) => {
          const sortedOperators = availableOperators.operators.slice().sort((a, b) => {
            if (a.name.toLowerCase() > b.name.toLowerCase()) {
              return 1;
            }
            if (a.name.toLowerCase() < b.name.toLowerCase()) {
              return -1;
            }
            return 0;
          });
          this.MnpOperators = [ ...sortedOperators, this.unlistedOperator ];
        })
    );

    this.subscriptions.add(
      this.formGroup.get('activationMnpInfo.operator').valueChanges
        .subscribe((operator) => {
          window.localStorage.removeItem('carrier-origin');
          if (operator && operator.toUpperCase() === WINDTRE_OPERATOR) {
            this.formGroup.get('activationMnpInfo.iccid').enable();
          } else {
            this.formGroup.get('activationMnpInfo.iccid').disable();
            this.formGroup.get('activationMnpInfo.iccid').reset();
          }
        })
    );

    this.subscriptions.add(
      this.formGroup.get('activationMnpInfo.operator').valueChanges
        .pipe(
          pairwise(),
          filter(([prev, next]) => next !== this.unlistedOperator.id),
          withLatestFrom(this.appStoreFacadeService.cart.cart$)
        ).subscribe(([[prev, next], cart]) => {
        this.manageOperatorChange(next, cart, prev);
        // Do another check for  ICCID validity
        this.formGroup.get('activationMnpInfo.iccid').updateValueAndValidity({ onlySelf: true });
      })
    );
    this.subscriptions.add(
      this.appStoreFacadeService.step.activationOtp$
        .subscribe( (stepData ) => {
          super.setFormGroupStepData(stepData);
        })
    );
    this.subscriptions.add(
      this.appStoreFacadeService.step.activationOtp$
        .pipe(debounceTime(150), first(Boolean))
        .subscribe(() => {
          this.sendAnalyticsData();
        })
    );
  }

  openEsimInfoModal() {
    this.appStoreFacadeService.modals.openModal(ModalType.Generic, {
      title: 'ENTITY.ACTIVATION-MNP-INFO.WHAT_IS_ESIM',
      message: 'ENTITY.ACTIVATION-MNP-INFO.WHAT_IS_ESIM_INFO',
    });
  }

  toggleVRTutorial() {
    this.showTutorial = !this.showTutorial;
  }

  public openModal() {
    this.shouldShowIccidModal = true;
    this.analyticsService.sendCustomPage('/popup/iccid');
  }

  public closeModal() {
    this.shouldShowIccidModal = false;
  }

  private getUpdatedCart(cart: Cart, newPrice: Price, priceType: TotalPriceEnum) {
    let oldPriceBeforeChange = 0;
    let newPriceAfterChange = 0;
    let hasChanged = false;
    const simPrice = cart.totalPrices.find(c => c.type === TotalPriceEnum.Sim).price.value || 0;
    const newTotalPrices = {...cart}.totalPrices.map(p => {
      const p2 = {...p};
      if (p2.type === priceType && p2.price.value !== newPrice.value) {
        hasChanged = true;
        oldPriceBeforeChange = (p2.price.value || 0) + simPrice;
        p2.price = newPrice;
        newPriceAfterChange = (newPrice.value || 0) + simPrice;
      }
      return p2;
    });
    const newCart = {
      ...cart,
      totalPrices: newTotalPrices,
      totalPrice: {
        ...cart.totalPrice,
        price: {
          ...cart.totalPrice.price,
          value: (cart.totalPrice.price.value || 0) - oldPriceBeforeChange + newPriceAfterChange
        }
      }
    };
    return {
      hasChanged,
      oldPriceBeforeChange,
      newPriceAfterChange,
      cart: newCart
    };
  }

  get shouldShowOperatorMessage(): boolean {
    return this.formGroup.get('activationMnpInfo.operator').value === this.unlistedOperator.id;
  }

  private manageOperatorChange(operatorId: string, cart: Cart, previousOperatorId: string) {
    if (operatorId) {
      const operatorInfo = this.MnpOperators.find(op => op.id === operatorId);
      if (operatorInfo && operatorInfo.configuration && operatorInfo.configuration.activationPrice) {
        const newCart = this.getUpdatedCart(cart, operatorInfo.configuration.activationPrice, TotalPriceEnum.Activation);
        if (newCart.hasChanged) {
          const pageName = `/popup/costo-attivazione-da-${newCart.oldPriceBeforeChange}-a-${newCart.newPriceAfterChange}`;
          this.analyticsService.sendCustomPage(pageName, {
            activation_price_new: newCart.newPriceAfterChange,
            activation_price_old: newCart.oldPriceBeforeChange
          });
          this.appStoreFacadeService.errors.openOperatorPriceChangeModal(
            {
              newPrice: newCart.newPriceAfterChange,
              oldPrice: newCart.oldPriceBeforeChange,
              currency: operatorInfo.configuration.activationPrice.currency
            },
            (proceed) => {
              if (proceed) {
                this.analyticsService.sendCustomEvent({
                  custom_category: 'popup costo attivazione',
                  custom_action: 'procedi',
                  custom_label: pageName
                });
                this.execService.verifyOperatorUpdate(operatorId)
                  .subscribe(updatedCart => {
                    this.appStoreFacadeService.entity.storeEntityValue(StaticEntityKey.Cart, updatedCart.cart);
                  });
              } else {
                this.analyticsService.sendCustomEvent({
                  custom_category: 'popup costo attivazione',
                  custom_action: 'chiudi',
                  custom_label: pageName
                });
                this.formGroup.patchValue({
                  activationMnpInfo: {
                    operator: previousOperatorId || null
                  }
                });
              }
            }
          );
        }
      }
    }
  }

  get isFormGroupInvalid() {
    if (this.formGroup) {
      const operator = this.formGroup.get('activationMnpInfo.operator').value;
      if (operator === WINDTRE_OPERATOR) {
        return this.formGroup.invalid || !this.formGroup.get('activationMnpInfo.iccid').value;
      }
      return this.formGroup.invalid || operator === 'NOTLISTED';
    }
    return true;
  }

  private manageOtpErrors() {
    this.subscriptions.add(
      this.appStoreFacadeService.errors.inputValidationErrors$
        .subscribe((inputValidationError: ValidationError) => {
          console.warn('manage error', inputValidationError);
          if (inputValidationError && inputValidationError.path && inputValidationError.path.includes('otp')) {
            let otpError;
            otpError = inputValidationError.message.split('|')[0].toUpperCase();
            if (otpError === 'OTP_MAX_LIMIT_REQUEST_REACHED') {
              this.appStoreFacadeService.navigation.navigateToWarningPage({
                title: 'ENTITY.ACTIVATION-MNP-INFO.OTP_ERROR.OTP_MAX_LIMIT_REQUEST_REACHED.TITLE',
                description: 'ENTITY.ACTIVATION-MNP-INFO.OTP_ERROR.OTP_MAX_LIMIT_REQUEST_REACHED.TEXT'
              });
              return;
            }
            console.warn('error modal ', otpError);
            this.openOtpModal(otpError, false);
          }
        })
    );
    this.subscriptions.add(
      this.appStoreFacadeService.errors.crossValidationErrors$
        .subscribe((validationError: ValidationError) => {
          console.warn('manage cross validation error', validationError);
          const validationErrorMessage = validationError && (validationError.message || validationError.path) || '';
          if (validationErrorMessage.includes('otp')) {
            let otpError;
            otpError = validationError.message.split('|')[0].toUpperCase();
            if (otpError === 'OTP_MAX_LIMIT_REQUEST_REACHED') {
              this.appStoreFacadeService.navigation.navigateToWarningPage({
                title: 'ENTITY.ACTIVATION-MNP-INFO.OTP_ERROR.OTP_MAX_LIMIT_REQUEST_REACHED.TITLE',
                description: 'ENTITY.ACTIVATION-MNP-INFO.OTP_ERROR.OTP_MAX_LIMIT_REQUEST_REACHED.TEXT'
              });
              return;
            }
            console.warn('error modal ', otpError);
            setTimeout(() => {
              this.openOtpModal(otpError, false);
            }, 150);
          }
        })
    );
  }

  getMnpData() {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.activationMnpInfo$
      ]).pipe(
        first((args) => args.every(Boolean))
      ).subscribe(([activationMnpInfo]: [EntityParsed]) => {
        this.mnpMsisdnInfo = (activationMnpInfo.value as unknown as ActivationMnpInfo).msisdn;
        this.mnpOperatorInfo = (activationMnpInfo.value as unknown as ActivationMnpInfo).operator;
        this.mnpEsimInfo = (activationMnpInfo.value as unknown as ActivationMnpInfo).esim;
      })
    );
  }

  private isFormDifferent() {
    if (this.formGroup.get('activationMnpInfo.operator').value !== this.mnpOperatorInfo ||
      this.formGroup.get('activationMnpInfo.msisdn').value !== this.mnpMsisdnInfo ||
      this.formGroup.get('activationMnpInfo.esim').value !== this.mnpEsimInfo) {
      return true;
    }
    return false;
  }
}
